/**
 * Flashka sound events → uisfx semantic cues — the single cross-platform
 * source of truth. Playback is one uisfx pack on both platforms (`zen` by
 * default, switchable in Settings): the web player synthesizes each cue from
 * the pack's deterministic recipe at runtime, and mobile plays the package's
 * pre-rendered exports of those SAME recipes
 * (`uisfx/sounds/<pack>/<cue>.mp3`), so the two are sonically identical.
 *
 * Call sites fire Flashka-domain events (`playSound('rating.easy')`); this
 * map is the only place that decides what that means. `CueName` comes from
 * the uisfx package types, so a typo'd or non-catalog cue is a compile error.
 *
 * A handful of sounds have no catalog equivalent, so they ship as local
 * assets in `src/sound/assets/` (see {@link LocalSoundAsset}) — such an entry
 * carries `asset` INSTEAD of `cue`.
 *
 * Deliberately NOT mapped:
 *  - Sounds on hover, scroll, passive layout changes, disabled controls, or
 *    background refreshes: banned outright. (The `hover` CUE is used as the
 *    timbre for `toast.show` — a deliberate choice for the quietest sound in
 *    the catalog — but nothing in the product plays on pointer hover.)
 */

import type { CueName, PackName } from 'uisfx';

import { pickTierName, type TierName } from '../oral-exam/score-tier';

/**
 * The pack every platform starts on, and the one the Settings dropdown marks
 * as the default. Users may switch: web synthesizes any pack's recipes at
 * runtime and mobile bundles every pack's pre-rendered exports (see the
 * generated `flashka-app/src/lib/sound/cue-assets.ts`), so changing this
 * constant only moves the default.
 */
export const DEFAULT_SOUND_PACK: PackName = 'zen';

/**
 * Display label for a pack in the Settings dropdown. Catalog keys are
 * lowercase identifiers (`zen`, `mechanical`) and stay that way on the wire and
 * in storage — this only capitalizes them for reading, and marks the committed
 * default so it is obvious which one it is.
 */
export function packLabel(pack: PackName): string {
  const capitalized = pack.charAt(0).toUpperCase() + pack.slice(1);
  return pack === DEFAULT_SOUND_PACK ? `${capitalized} (Default)` : capitalized;
}

/**
 * The catalogue's packs with {@link DEFAULT_SOUND_PACK} moved to the front —
 * the order the Settings dropdown lists them in, on both platforms. Everything
 * else keeps its catalogue order, so the list is otherwise unchanged.
 */
export function packsDefaultFirst<T extends { name: PackName }>(
  packs: readonly T[],
): T[] {
  return [
    ...packs.filter((p) => p.name === DEFAULT_SOUND_PACK),
    ...packs.filter((p) => p.name !== DEFAULT_SOUND_PACK),
  ];
}

/**
 * Sounds we ship ourselves because the catalog has no equivalent. Rendered
 * by `shared/scripts/generate-card-flip.mjs` into `src/sound/assets/` and
 * loaded by both platforms (Vite import / Metro require).
 *
 * `card.flip` fires on every card in a session, so it is a filtered-noise
 * page-turn built to sit under everything in the pack: 0.16 s, band-limited
 * around 3.2 kHz with a downward sweep, quick attack and a release that
 * trails to silence. Variation A was chosen after A/B; the other two
 * candidates have been deleted.
 */
export type LocalSoundAsset = 'card-flip-a';

export const LOCAL_SOUND_ASSETS: LocalSoundAsset[] = ['card-flip-a'];

/** What a platform adapter is asked to play: a catalog cue or a local file. */
export type SoundSource =
  | { kind: 'cue'; cue: CueName }
  | { kind: 'asset'; asset: LocalSoundAsset };

/** Stable string form of a source — `'success'` or `'asset:card-flip-b'`. */
export function sourceKey(source: SoundSource): string {
  return source.kind === 'cue' ? source.cue : `asset:${source.asset}`;
}

/** Inverse of {@link sourceKey} (used by the audition story's dropdowns). */
export function parseSourceKey(key: string): SoundSource {
  return key.startsWith('asset:')
    ? { kind: 'asset', asset: key.slice('asset:'.length) as LocalSoundAsset }
    : { kind: 'cue', cue: key as CueName };
}

export type SoundEvent =
  // Study loop (spaced repetition AND fast review — identical set)
  | 'card.flip'
  | 'rating.impossible'
  | 'rating.hard'
  | 'rating.ok'
  | 'rating.easy'
  | 'session.complete'
  | 'streak.unlocked'
  | 'mastery.levelup'
  // Practice (quiz / mock exam / oral exam / case study)
  | 'quiz.answer.correct'
  | 'quiz.answer.wrong'
  | 'exam.answer.select'
  | 'quiz.milestone'
  | 'answer.send'
  | 'evaluation.correct'
  | 'evaluation.wrong'
  | 'result.low'
  | 'result.medium'
  | 'result.high'
  // Content
  | 'delete.trash'
  | 'cards.progress'
  | 'map.saved'
  | 'edit.undo'
  | 'edit.redo'
  | 'dnd.drop'
  | 'dnd.invalid'
  | 'typing'
  // System
  | 'toast.show'
  | 'notification.ding'
  | 'connection.online'
  | 'connection.offline';

type SoundEventBase = {
  /** Minimum ms between two plays of this event (enforced in core.ts for
   *  both platforms — notifications and progress are throttled hard). */
  cooldownMs: number;
  /** Playback gain multiplier. Catalog cues carry their own per-cue level;
   *  local assets use this to sit correctly against them. */
  gain?: number;
};

/**
 * An event plays EITHER a catalog cue or a local asset — never both, and
 * never neither. The union makes an asset-only entry (like the card reveal,
 * which the catalog has no cue for) expressible without a dummy fallback.
 */
export type SoundEventDef =
  | (SoundEventBase & { cue: CueName; asset?: never })
  | (SoundEventBase & { asset: LocalSoundAsset; cue?: never });

export const EVENT_CUES: Record<SoundEvent, SoundEventDef> = {
  // Study loop. The reveal is a LOCAL asset — the catalog has no page-turn
  // cue. Fires on every card, so `gain` keeps it far below the pack cues.
  'card.flip': { asset: 'card-flip-a', cooldownMs: 120, gain: 0.55 },
  'rating.impossible': { cue: 'error', cooldownMs: 100 },
  'rating.hard': { cue: 'progress-step', cooldownMs: 100 },
  'rating.ok': { cue: 'play', cooldownMs: 100 },
  'rating.easy': { cue: 'success', cooldownMs: 100 },
  // Fires when the END screen itself appears, not when the queue empties —
  // on a shared deck the Claim/Reveal screens come first and own their own
  // cues, so the fanfare must wait for the summary to be the visible screen.
  'session.complete': { cue: 'achievement', cooldownMs: 1000 },
  // The daily-streak unlock screen (10 cards studied) appearing mid-session.
  'streak.unlocked': { cue: 'streak', cooldownMs: 1000 },
  // The Claim Mastery Points screen appearing — the first of the two
  // study-group end screens, ahead of the summary's `session.complete`.
  'mastery.levelup': { cue: 'level-up', cooldownMs: 1000 },

  // Practice. Correctness is known the moment a quiz answer is clicked, so
  // the outcome cue fires immediately — and ALONE (no `select` tick beneath
  // it). Mock exams are the mirror image: they get the neutral `select` tick
  // and never a right/wrong cue (see `exam.answer.select`).
  'quiz.answer.correct': { cue: 'success', cooldownMs: 80 },
  'quiz.answer.wrong': { cue: 'error', cooldownMs: 80 },
  // Mock exams withhold correctness until submission (`correctImmediately`
  // is false), so a right/wrong cue there would leak the answer. The neutral
  // selection tick is the honest feedback — and it fires on EVERY selection,
  // including changing your mind, since that is a deliberate act.
  'exam.answer.select': { cue: 'select', cooldownMs: 80 },
  'quiz.milestone': { cue: 'streak', cooldownMs: 500 },
  'answer.send': { cue: 'send', cooldownMs: 300 },
  'evaluation.correct': { cue: 'success', cooldownMs: 250 },
  'evaluation.wrong': { cue: 'refund', cooldownMs: 250 },
  'result.low': { cue: 'refund', cooldownMs: 1000 },
  'result.medium': { cue: 'retry', cooldownMs: 1000 },
  'result.high': { cue: 'achievement', cooldownMs: 1000 },

  // Content. `delete` fires only after the removal is committed.
  'delete.trash': { cue: 'delete', cooldownMs: 250 },
  'cards.progress': { cue: 'progress-step', cooldownMs: 750 },
  'map.saved': { cue: 'checkpoint', cooldownMs: 500 },
  'edit.undo': { cue: 'undo', cooldownMs: 200 },
  'edit.redo': { cue: 'redo', cooldownMs: 200 },
  'dnd.drop': { cue: 'drag-start', cooldownMs: 150 },
  'dnd.invalid': { cue: 'invalid-drop', cooldownMs: 300 },
  // Text entry. Deliberately NOT throttled: the cue is one tick per local
  // `input` event, so it tracks the user's own keystrokes exactly. Kept very
  // quiet — it is the most frequent sound in the product by far.
  typing: { cue: 'typing', cooldownMs: 0, gain: 0.5 },

  // System. A toast borrows the catalog's quietest cue (`hover`) as a timbre
  // — nothing in the product plays on pointer hover. Connection changes are
  // heavily throttled so a flapping network never becomes a metronome.
  'toast.show': { cue: 'hover', cooldownMs: 1000 },
  'notification.ding': { cue: 'notification', cooldownMs: 2000 },
  'connection.online': { cue: 'connect', cooldownMs: 5000 },
  'connection.offline': { cue: 'disconnect', cooldownMs: 5000 },
};

export const SOUND_EVENTS = Object.keys(EVENT_CUES) as SoundEvent[];

/**
 * There are no looping sounds. Earlier passes had continuous beds under
 * generation, chat streaming and the oral-exam recorder; all were removed as
 * noise without information. If a bed is ever wanted again, it needs a handle
 * registry with stop-on-every-exit semantics — see git history for the
 * previous implementation rather than reviving a stub.
 */

const RESULT_SOUND_FOR_TIER: Record<TierName, SoundEvent> = {
  red: 'result.low',
  amber: 'result.medium',
  green: 'result.high',
  purple: 'result.high',
};

/**
 * Exam/case-study/quiz result event for a 0-100 score, bucketed by the SAME
 * `pickTier` thresholds that color the score UI (98/70/50) — audio and
 * visuals can never disagree.
 */
export function resultSoundForPercent(percent: number): SoundEvent {
  return RESULT_SOUND_FOR_TIER[pickTierName(percent)];
}
